import { createSlice } from "@reduxjs/toolkit";
import {
  getActividadesAplicacionThunk,
  getActividadAplicacionByIdThunk,
  updateActividadAplicacionThunk,
  toggleActividadAplicacionActiveThunk,
  createMultiplesActividadesAplicacionThunk,
} from "./actividadesAplicacion.thunks";

const initialState = {
  list: [],
  loading: false,
  error: null,
  page: 1,
  pageSize: 10,
  count: 0,
  next: null,
  previous: null,
  maxPageReached: 1,
  search: "",
  talentoFilter: "",

  selectedActividad: null,
  selectedActividadLoading: false,
  selectedActividadError: null,
  viewModalOpen: false,

  formModalOpen: false,
  formMode: "create",
  formLoading: false,
  formError: null,

  confirmModalOpen: false,
  toggleActividadId: null,
  toggling: false,
};

const actividadesAplicacionSlice = createSlice({
  name: "actividadesAplicacion",
  initialState,
  reducers: {
    clearActividadesAplicacionError: (state) => {
      state.error = null;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setSearch: (state, action) => {
      state.search = action.payload;
      state.page = 1;
    },
    setTalentoFilter: (state, action) => {
      state.talentoFilter = action.payload;
      state.page = 1;
    },
    clearFilters: (state) => {
      state.search = "";
      state.talentoFilter = "";
      state.page = 1;
    },
    openViewModal: (state) => {
      state.viewModalOpen = true;
    },
    closeViewModal: (state) => {
      state.viewModalOpen = false;
      state.selectedActividad = null;
    },
    openFormModal: (state, action) => {
      state.formModalOpen = true;
      state.formMode = action.payload?.mode || "create";
      if (action.payload?.actividad) {
        state.selectedActividad = action.payload.actividad;
      }
      state.formError = null;
    },
    closeFormModal: (state) => {
      state.formModalOpen = false;
      state.selectedActividad = null;
      state.formError = null;
    },
    openConfirmToggle: (state, action) => {
      state.confirmModalOpen = true;
      state.toggleActividadId = action.payload.id;
    },
    closeConfirmToggle: (state) => {
      state.confirmModalOpen = false;
      state.toggleActividadId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET ALL
      .addCase(getActividadesAplicacionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getActividadesAplicacionThunk.fulfilled, (state, action) => {
        const { results, count, next, previous } = action.payload;
        state.loading = false;
        state.list = results;
        state.count = count;
        state.next = next;
        state.previous = previous;
        state.maxPageReached = Math.max(state.maxPageReached, state.page);
      })
      .addCase(getActividadesAplicacionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // GET BY ID
      .addCase(getActividadAplicacionByIdThunk.pending, (state) => {
        state.selectedActividadLoading = true;
        state.selectedActividadError = null;
      })
      .addCase(getActividadAplicacionByIdThunk.fulfilled, (state, action) => {
        state.selectedActividadLoading = false;
        state.selectedActividad = action.payload;
      })
      .addCase(getActividadAplicacionByIdThunk.rejected, (state, action) => {
        state.selectedActividadLoading = false;
        state.selectedActividadError = action.payload;
      })

      // CREATE MULTIPLES
      .addCase(createMultiplesActividadesAplicacionThunk.pending, (state) => {
        state.formLoading = true;
        state.formError = null;
      })
      .addCase(createMultiplesActividadesAplicacionThunk.fulfilled, (state) => {
        state.formLoading = false;
        state.formModalOpen = false;
      })
      .addCase(
        createMultiplesActividadesAplicacionThunk.rejected,
        (state, action) => {
          state.formLoading = false;
          state.formError = action.payload;
        },
      )

      // UPDATE
      .addCase(updateActividadAplicacionThunk.pending, (state) => {
        state.formLoading = true;
        state.formError = null;
      })
      .addCase(updateActividadAplicacionThunk.fulfilled, (state, action) => {
        state.formLoading = false;
        const index = state.list.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) state.list[index] = action.payload;
        state.formModalOpen = false;
      })
      .addCase(updateActividadAplicacionThunk.rejected, (state, action) => {
        state.formLoading = false;
        state.formError = action.payload;
      })

      // TOGGLE ACTIVE
      .addCase(toggleActividadAplicacionActiveThunk.pending, (state) => {
        state.toggling = true;
        // Optimistic update
        const index = state.list.findIndex(
          (a) => a.id === state.toggleActividadId,
        );
        if (index !== -1) {
          state.list[index] = {
            ...state.list[index],
            is_active: !state.list[index].is_active,
          };
        }
      })
      .addCase(
        toggleActividadAplicacionActiveThunk.fulfilled,
        (state, action) => {
          state.toggling = false;
          state.confirmModalOpen = false;
          const index = state.list.findIndex((a) => a.id === action.payload.id);
          if (index !== -1) {
            state.list[index] = action.payload;
          }
        },
      )
      .addCase(
        toggleActividadAplicacionActiveThunk.rejected,
        (state, action) => {
          state.toggling = false;
          state.error = action.payload;
          // Revertir optimistic update
          const index = state.list.findIndex(
            (a) => a.id === state.toggleActividadId,
          );
          if (index !== -1) {
            state.list[index] = {
              ...state.list[index],
              is_active: !state.list[index].is_active,
            };
          }
        },
      );
  },
});

export const {
  clearActividadesAplicacionError,
  setPage,
  setSearch,
  setTalentoFilter,
  clearFilters,
  openViewModal,
  closeViewModal,
  openFormModal,
  closeFormModal,
  openConfirmToggle,
  closeConfirmToggle,
} = actividadesAplicacionSlice.actions;

export default actividadesAplicacionSlice.reducer;
